
<div class="print-wrapper bg-gray-600 min-h-screen pb-32">
  @if(isLoading()) {
    <div class="flex flex-col items-center justify-center h-screen text-white no-print">
      <i class="fa-solid fa-spinner animate-spin text-5xl mb-4"></i>
      <p class="text-xl">กำลังจัดเตรียมเอกสาร...</p>
    </div>
  } @else {
    <div class="paper-container flex justify-center pt-8">
       <!-- A4 Certificate Paper -->
       <div class="A4-cert-paper relative shadow-2xl bg-white text-black w-full max-w-[210mm] min-h-[297mm] mx-auto p-[2.5cm] flex flex-col font-sarabun">
           
           <!-- Watermark (Optional) -->
           @if(clinicSettings()?.certificateBgUrl) {
               <img [src]="clinicSettings()!.certificateBgUrl" class="absolute inset-0 w-full h-full object-cover opacity-20 pointer-events-none z-0">
           }

           <!-- 1. Header Section -->
           <div class="relative z-10 mb-8">
               <div class="flex items-start justify-between">
                   <!-- Logo -->
                   <div class="w-24 h-24 flex-shrink-0">
                       @if(clinicSettings()?.certificateLogoUrl) {
                           <img [src]="clinicSettings()!.certificateLogoUrl" class="w-full h-full object-contain">
                       }
                   </div>
                   <!-- Clinic Info -->
                   <div class="text-right">
                       <h1 class="text-2xl font-bold text-emerald-800 uppercase tracking-wide">ใบรับรองแพทย์</h1>
                       <h2 class="text-lg font-bold text-gray-600">MEDICAL CERTIFICATE</h2>
                       <div class="mt-2 text-sm text-gray-500 leading-tight w-72 ml-auto">
                           {{ clinicSettings()?.addressText || 'ที่อยู่สถานพยาบาล' }}
                       </div>
                   </div>
               </div>
               
               <div class="mt-6 flex justify-end border-b-2 border-emerald-800 pb-2">
                    <div class="text-right">
                        <p class="text-sm font-semibold text-emerald-900">Reference No: <span class="font-mono text-black">{{ certData()?.id?.substring(0,8) | uppercase }}</span></p>
                    </div>
               </div>
           </div>

           <!-- 2. Patient Profile (Full Width - No Photo) -->
           <div class="relative z-10 mb-8 px-4 py-6 border border-gray-200 rounded-lg bg-gray-50/50">
              <div class="flex flex-col gap-4 text-base leading-relaxed">
                  <!-- Row 1 -->
                  <div class="flex flex-col sm:flex-row sm:justify-between gap-4">
                      <div class="flex items-baseline w-full sm:w-[60%]">
                          <span class="font-bold text-gray-700 whitespace-nowrap mr-2">ชื่อ-นามสกุล:</span> 
                          <span class="border-b border-dotted border-gray-400 px-2 flex-grow text-lg font-bold text-blue-900">{{ patientName }}</span>
                      </div>
                      <div class="flex items-baseline w-full sm:w-[35%]">
                          <span class="font-bold text-gray-700 whitespace-nowrap mr-2">อายุ:</span> 
                          <span class="border-b border-dotted border-gray-400 px-2 flex-grow text-center font-bold">{{ patientAge }}</span> 
                          <span class="ml-2">ปี</span>
                      </div>
                  </div>
                  <!-- Row 2 -->
                  <div class="flex items-baseline">
                      <span class="font-bold text-gray-700 whitespace-nowrap mr-2">เลขบัตรประชาชน:</span> 
                      <span class="border-b border-dotted border-gray-400 px-2 font-mono tracking-widest text-lg">{{ certData()?.customer?.citizenId || '-' }}</span>
                  </div>
                  <!-- Row 3 -->
                  <div class="flex items-baseline">
                      <span class="font-bold text-gray-700 whitespace-nowrap mr-2 self-start pt-1">ที่อยู่:</span> 
                      <span class="border-b border-dotted border-gray-400 px-2 flex-grow leading-normal break-words">{{ certData()?.customer?.address || '-' }}</span>
                  </div>
              </div>
           </div>

           <!-- 3. Medical & Prescription Section -->
           <div class="relative z-10 flex-grow text-base space-y-8 px-2">
               
               <!-- Diagnosis -->
               <div>
                   <h3 class="font-bold text-lg mb-2 text-emerald-800 border-b border-gray-200 pb-1 w-fit">การวินิจฉัยโรค (Diagnosis)</h3>
                   <div class="p-4 border border-gray-300 rounded-lg bg-white min-h-[60px] text-lg font-medium text-gray-800 shadow-sm">
                       {{ diagnosis }}
                   </div>
               </div>

               <!-- Prescribed Item (Smart Summary) -->
               <div>
                   <h3 class="font-bold text-lg mb-4 text-emerald-800 border-b border-gray-200 pb-1 w-fit">รายการที่ได้รับอนุมัติ (Prescribed Item)</h3>
                   <div class="pl-6 border-l-4 border-emerald-500 py-4 bg-emerald-50/30 rounded-r-lg">
                       <p class="text-xl font-bold text-gray-800 mb-4">
                           {{ prescriptionText }}
                       </p>
                       <div class="flex flex-col sm:flex-row gap-6 sm:items-center">
                           <div class="bg-white px-4 py-2 rounded border border-gray-200 shadow-sm min-w-[150px]">
                               <span class="text-sm text-gray-500 block">จำนวนรวม (Total)</span>
                               <span class="text-2xl font-black text-emerald-700 mx-1">{{ totalAmount }}</span> 
                               <span class="font-bold text-gray-700">{{ amountUnit }}</span>
                           </div>
                           <div class="bg-white px-4 py-2 rounded border border-gray-200 shadow-sm min-w-[150px]">
                               <span class="text-sm text-gray-500 block">ระยะเวลา (Duration)</span>
                               <span class="text-2xl font-black text-blue-700 mx-1">{{ durationDays }}</span> 
                               <span class="font-bold text-gray-700">วัน</span>
                           </div>
                       </div>
                   </div>
               </div>

           </div>

           <!-- 4. Signatures & QR Code -->
           <div class="relative z-10 mt-auto pt-10">
               <div class="flex justify-between items-end">
                   <!-- QR Code Verification (HIDDEN) -->
                   <div class="text-center w-24">
                       <!-- QR Code Hidden per request -->
                   </div>
                   
                   <!-- Doctor Signature (Fixed Alignment) -->
                   <div class="text-center w-72">
                       <div class="relative h-20 w-full flex items-end justify-center mb-1">
                           @if(signatureUrl()) {
                               <!-- Signature Image sits on top of the line -->
                               <img [src]="signatureUrl()" class="absolute bottom-0 h-24 object-contain mix-blend-multiply opacity-90 z-10 pb-1">
                           }
                           <!-- The Dotted Line -->
                           <div class="border-b-2 border-dotted border-gray-400 w-full absolute bottom-0 z-0"></div>
                       </div>
                       
                       <p class="font-bold text-lg text-blue-900 mt-2">{{ doctorName }}</p>
                       <p class="text-xs text-gray-500 uppercase tracking-widest mb-1">Attending Physician</p>
                       <p class="text-sm">ใบอนุญาตเลขที่: <span class="font-mono font-bold">{{ clinicSettings()?.licenseNumber }}</span></p>
                       <p class="text-sm mt-1 text-gray-600">วันที่: {{ thaiDate }}</p>
                   </div>
               </div>
           </div>
           
           <!-- Footer Note -->
           <div class="relative z-10 mt-8 pt-4 border-t border-gray-200 flex justify-between text-[10px] text-gray-400">
               <span>เอกสารนี้ออกโดยระบบ CHUAMKAN Platform สำหรับคลินิกกัญชาทางการแพทย์</span>
               <span>Official Medical Document</span>
           </div>

       </div>
    </div>
  }
</div>

<!-- Floating Action Bar (No Print) -->
<div class="fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-700 p-4 shadow-lg z-[100] no-print">
    <div class="max-w-screen-xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-3">
        <div class="text-white">
            <h3 class="font-bold text-emerald-400">Medical Certificate Preview</h3>
            <p class="text-xs opacity-70">A4 Standard Format</p>
        </div>
        <div class="flex gap-3 w-full sm:w-auto">
             <!-- Close Button -->
             @if(prescriptionIdInput()) {
                <button (click)="onClose()" class="flex-1 sm:flex-none bg-slate-700 hover:bg-slate-600 text-white px-6 py-2 rounded-lg font-bold transition-colors">
                    <i class="fa-solid fa-times mr-2"></i> ปิด
                </button>
             }

             <button (click)="downloadImage()" [disabled]="isGeneratingImage()" class="flex-1 sm:flex-none bg-purple-600 hover:bg-purple-500 text-white px-6 py-2 rounded-lg font-bold shadow-lg flex items-center justify-center gap-2 disabled:opacity-50 transition-all">
                @if(isGeneratingImage()) { <i class="fa-solid fa-spinner animate-spin"></i> } @else { <i class="fa-solid fa-image"></i> }
                <span>Save PNG</span>
            </button>
            <button onclick="window.print()" class="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-500 text-white px-8 py-2 rounded-lg font-bold shadow-lg flex items-center justify-center gap-2 transition-all">
                <i class="fa-solid fa-print"></i>
                <span>Print PDF</span>
            </button>
        </div>
    </div>
</div>
